import { useState } from 'react';
import { t } from 'ttag';
import classNames from 'classnames';
import dayjs from 'dayjs';

import UsersIcon from '@svg/users.svg';
import SvgPurchases from '@svg/purchases.svg';
import SvgPointer from '@svg/pointer.svg';
import TimeIcon from '@svg/time.svg';
import BagsIcon from '@svg/bags.svg';
import SvgCheck from '@svg/check.svg';
import ArrowRightIcon from '@svg/arrows-right.svg';
import { Title } from '@ui/Title';
import { Image } from '@ui/Image';
import { Button } from '@ui/Button';
import { SellerRatingAverage } from '@components/SellerRatingAverage';
import { ShowRealNameEnum, GenderEnum } from '@models';
import { rupluralize } from '@utils/i18n';
import { NOT_SELECTED_CITY_ID } from '@constants';
import { Alert } from '@ui/Alert';
import { convertLastVisitToText } from '@utils/common';
import { TopSellerBadge } from '@components/TopSellerBadge';
import { TopSellerInfoModal } from '@components/TopSellerInfoModal';

import s from './UserProfile.sss';

type SellerRatingInfo = {
  overallRating: string
  reviewsCount: number
};

type UserProfileProps = {
  className?: string;
  id: number;
  username: string;
  thumbnail: string;
  firstName: Maybe<string>;
  lastName: Maybe<string>;
  showRealName: Maybe<ShowRealNameEnum>;
  followersCount: number;
  sellerRatingInfo: Maybe<SellerRatingInfo>
  soldProducts: number;
  city: Maybe<{ name: string, id: number }>;
  lastVisit: Maybe<number>;
  middleResponseTime: Maybe<string>;
  gender: Maybe<GenderEnum>;
  deltaDateJoined: string;
  isSubscribed: boolean;
  isTopSeller: boolean;
  isFollowLoading?: boolean;
  onFollowToggle: () => void;
  fraudWarning: boolean;
  productsCount: Maybe<number>;
  ban: Maybe<{
    reason: string,
    isActive: boolean,
  }>,
  shopName: Maybe<string>,
  viewer?: {
    id: number,
  }
  hasRecentActivity: boolean,
  orderRate: Maybe<string>,
};

export const UserProfile: React.FC<UserProfileProps> = ({
  id,
  username,
  thumbnail,
  firstName,
  lastName,
  showRealName,
  followersCount,
  sellerRatingInfo,
  soldProducts,
  city,
  lastVisit,
  middleResponseTime,
  gender,
  isSubscribed,
  isTopSeller,
  deltaDateJoined,
  viewer,
  isFollowLoading,
  onFollowToggle,
  fraudWarning,
  ban,
  className,
  productsCount,
  shopName,
  hasRecentActivity,
  orderRate,
}) => {
  const [isTopSellerInfoModalOpen, setIsTopSellerInfoModalOpen] = useState(false);

  return (
    <>
      <div className={classNames(s.root, className)}>
        <div className={s.avatarOuter}>
          <Image
            className={s.avatar}
            src={thumbnail}
            alt={username}
          />
          {
          lastVisit
            && <div className={s.lastVisit}>{convertLastVisitToText(dayjs.unix(lastVisit))}</div>
        }
        </div>
        <div className={s.rightSide}>
          <div className={s.profileInfoGroup}>
            <Title type="h5" bold={false} className={s.name}>
              {shopName || username}
              {
            firstName
            && lastName
            && (
              showRealName === ShowRealNameEnum.ShowRealNameForEveryone
              || (showRealName === ShowRealNameEnum.ShowRealNameForRegisteredUsers && viewer?.id)
            )
            && `(${firstName} ${lastName})`
          }
            </Title>

            <SellerRatingAverage
              overallRating={sellerRatingInfo?.overallRating || 0}
              reviewsCount={sellerRatingInfo?.reviewsCount || 0}
              sellerId={id}
              className={s.rating}
            />

            {isTopSeller && (
              <Button
                className={s.topSellerButton}
                theme="text"
                onClick={() => setIsTopSellerInfoModalOpen(true)}
              >
                <TopSellerBadge className={s.topSellerBadge} />
                <ArrowRightIcon
                  className={s.topSellerArrow}
                  width="16"
                  height="16"
                />
              </Button>
            )}

            <div className={s.detailInfo}>
              <p className={s.infoBlock}>
                <SvgCheck className={s.icon} />
                <span className={s.textInfo}>
                  {t`На Шафі ${deltaDateJoined}`}
                </span>
              </p>
              {city && city.id !== NOT_SELECTED_CITY_ID && (
              <div className={s.infoBlock}>
                <SvgPointer className={s.icon} />
                <span className={s.textInfo}>{city.name}</span>
              </div>
              )}
              <div className={s.infoBlock}>
                <UsersIcon
                  className={s.icon}
                  width='16'
                  height='16'
                />
                <span className={s.textInfo}>
                  {followersCount}
                  {' '}
                  {rupluralize(followersCount, [t`підписник`, t`підписника`, t`підписників`])}
                </span>
              </div>
              <div className={s.infoBlock}>
                <SvgPurchases className={s.icon} width={18} height={18} />
                <span className={s.textInfo}>
                  {soldProducts}
                  {' '}
                  {rupluralize(soldProducts, [t`продаж`, t`продажі`, t`продажів`])}
                </span>
              </div>

              {middleResponseTime && (
              <div className={s.infoBlock}>
                <TimeIcon
                  className={s.icon}
                  width='16'
                  height='16'
                />
                <span className={s.textInfo}>{middleResponseTime}</span>
              </div>
              )}
              {orderRate && (
              <div className={s.infoBlock}>
                <BagsIcon
                  className={s.icon}
                  width='16'
                  height='16'
                />
                <span className={s.textInfo}>{t`${orderRate}% виконаних замовлень`}</span>
              </div>
              )}
            </div>

            <div className={s.warnings}>
              {fraudWarning && (
              <Alert theme="warning">
                {t`Увага! За даним продавцем йде розгляд.`}
              </Alert>
              )}
              {(!!productsCount && !hasRecentActivity) && (
              <Alert theme="warning">
                {t`Продавець`}
                {' '}
                {gender === GenderEnum.GenderMale ? t`був` : t`була`}
                {' '}
                {t`На Шафі більше 30 днів тому`}
              </Alert>
              )}
              {ban?.isActive && <Alert theme="warning">{ban.reason}</Alert>}
            </div>
          </div>
          {viewer && viewer.id !== id && (
          <Button
            className={s.followButton}
            theme={isSubscribed ? 'secondary' : 'primary'}
            disabled={isFollowLoading}
            onClick={onFollowToggle}
          >
            {isSubscribed ? t`Ви підписані` : t`Підписатися`}
          </Button>
          )}
        </div>
      </div>
      <TopSellerInfoModal
        isOpen={isTopSellerInfoModalOpen}
        onRequestClose={() => setIsTopSellerInfoModalOpen(false)}
      />
    </>
  );
};
